import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { useUser, useCurrentTenant } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import OutreachEngagementIcons from '@/components/common/OutreachEngagementIcons';
import { getOutboxDraftHeadline, isLinkedInOutboxChannel } from '@/lib/outboxChannel';
import { useTranslation } from '@/components/utils/translations';
import { Loader2, ExternalLink, AlertCircle, Calendar, Sparkles, Mail, Phone, Linkedin } from 'lucide-react';

const API_BASE_URL = (() => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'https://api.neogtm.com';
  }
  return 'https://api.neogtm.com';
})();

const CHANNEL_ICONS = { email: Mail, call: Phone, linkedin: Linkedin };
const PLAYBOOK_SOURCES = new Set(['playbook', 'diy_playbook']);

function isAdHocSource(source) {
  const s = String(source || '').toLowerCase();
  if (!s || s === 'manual') return true;
  return !PLAYBOOK_SOURCES.has(s);
}

function parseOutreachRows(result) {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.rows)) return result.rows;
  if (Array.isArray(result?.messages)) return result.messages;
  if (Array.isArray(result?.data)) return result.data;
  return [];
}

function normalizeAdHocFromSummary(result) {
  const block = result?.ad_hoc_messages ?? result?.ad_hoc ?? null;
  if (!block) return { personas: [], total: 0 };
  if (Array.isArray(block)) {
    const total = block.reduce(
      (n, p) => n + (p.messages?.length || p.message_count || 0),
      0,
    );
    return { personas: block, total: total || block.length };
  }
  const personas = Array.isArray(block.personas) ? block.personas : [];
  const total =
    block.total ??
    personas.reduce((n, p) => n + (p.message_count || p.messages?.length || 0), 0);
  return { personas, total };
}

function formatOutreachMessage(msg) {
  return {
    id: msg.id ?? msg.step_id,
    step_id: msg.step_id,
    subject: msg.subject || 'Manual completion',
    channel: msg.channel || 'email',
    sent_at: msg.sent_at ?? msg.completed_at,
    source: msg.source || 'manual',
    opened_at: msg.opened_at,
    clicked_at: msg.clicked_at,
    replied_at: msg.replied_at,
  };
}

function adHocSourceLabel(source, t) {
  switch (source) {
    case 'today_plan':
      return t('todaysPlanTitle');
    case 'recommendation':
      return t('acctNeoRecommendation');
    case 'profile_outreach':
      return t('acctProfileOutreach');
    case 'ad_hoc':
      return t('adHoc');
    case 'diy_playbook':
      return t('acctDiyPlaybook');
    case 'playbook':
      return t('playbook');
    default:
      return t('adHoc');
  }
}

function countAdHocMessages(personas) {
  return (personas || []).reduce(
    (n, p) => n + (p.messages?.length || p.message_count || 0),
    0,
  );
}

export default function PlaybooksTab({
  accountId,
  personas: accountPersonas = [],
  currentTenant: currentTenantProp,
  onDataLoaded,
}) {
  const { t } = useTranslation();
  const contextUser = useUser();
  const contextTenant = useCurrentTenant();
  const currentTenant = currentTenantProp || contextTenant;
  const tenantId = resolveEffectiveTenant(
    contextUser?.role === 'admin' ? localStorage.getItem('admin-active-tenant') : null,
    currentTenant,
    contextUser,
    'instructure',
  );
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [apiAvailable, setApiAvailable] = useState(true);
  const [fallbackAdHocPersonas, setFallbackAdHocPersonas] = useState([]);
  const [fallbackScheduled, setFallbackScheduled] = useState([]);
  const [loadingAdHocFallback, setLoadingAdHocFallback] = useState(false);

  const onDataLoadedRef = useRef(onDataLoaded);
  useEffect(() => {
    onDataLoadedRef.current = onDataLoaded;
  });

  const reportCount = useCallback((summary, adHocPersonas, scheduled) => {
    const active = summary?.active_playbooks?.length || 0;
    const completed = summary?.completed_playbooks?.length || 0;
    const adHoc = countAdHocMessages(adHocPersonas);
    const scheduledCount = scheduled?.length || 0;
    onDataLoadedRef.current?.(active + completed + (adHoc > 0 ? 1 : 0) + (scheduledCount > 0 ? 1 : 0));
  }, []);

  const loadAdHocFallback = useCallback(async () => {
    const personaList = (accountPersonas || []).filter((p) => p.person_id || p.id);
    if (!personaList.length || !tenantId || !contextUser?.id) {
      setFallbackAdHocPersonas([]);
      setFallbackScheduled([]);
      return;
    }

    setLoadingAdHocFallback(true);
    try {
      const userParam = encodeURIComponent(contextUser.id);
      const results = await Promise.allSettled(
        personaList.map(async (persona) => {
          const personId = persona.person_id || persona.id;
          const [outreachRes, playbooksRes] = await Promise.all([
            fetch(
              `${API_BASE_URL}/api/analytics-v2/personas/${encodeURIComponent(personId)}/all-outreach?tenant_id=${encodeURIComponent(tenantId)}&user_id=${userParam}`,
              { credentials: 'include' },
            ),
            fetch(
              `${API_BASE_URL}/api/personas/${encodeURIComponent(personId)}/all-playbooks?tenant_id=${encodeURIComponent(tenantId)}&user_id=${userParam}`,
              { credentials: 'include' },
            ),
          ]);

          let messages = [];
          if (outreachRes.ok) {
            const outreachJson = await outreachRes.json();
            messages = parseOutreachRows(outreachJson)
              .filter((m) => isAdHocSource(m.source))
              .map(formatOutreachMessage);
          }

          let scheduled = [];
          if (playbooksRes.ok) {
            const playbooksJson = await playbooksRes.json();
            const upcoming = Array.isArray(playbooksJson?.scheduled_upcoming)
              ? playbooksJson.scheduled_upcoming
              : [];
            scheduled = upcoming.filter(
              (s) =>
                s.source === 'ad_hoc' ||
                s.is_ad_hoc ||
                s.is_scheduled_ad_hoc ||
                s.sequence_id === 'ad_hoc_scheduled',
            );
          }

          return { persona, messages, scheduled };
        }),
      );

      const adHocByPerson = [];
      const scheduledFlat = [];

      results.forEach((result) => {
        if (result.status !== 'fulfilled') return;
        const { persona, messages, scheduled } = result.value;
        if (messages.length > 0) {
          adHocByPerson.push({
            person_id: persona.person_id || persona.id,
            first_name: persona.first_name,
            last_name: persona.last_name,
            role_title: persona.role_title,
            message_count: messages.length,
            messages,
          });
        }
        scheduled.forEach((s) => {
          scheduledFlat.push({
            ...s,
            person_name: `${persona.first_name || ''} ${persona.last_name || ''}`.trim(),
            person_id: persona.person_id || persona.id,
          });
        });
      });

      setFallbackAdHocPersonas(adHocByPerson);
      setFallbackScheduled(scheduledFlat);
    } catch (err) {
      console.warn('Account ad-hoc fallback fetch failed:', err?.message);
      setFallbackAdHocPersonas([]);
      setFallbackScheduled([]);
    } finally {
      setLoadingAdHocFallback(false);
    }
  }, [accountPersonas, tenantId, contextUser?.id]);

  const loadPlaybookSummary = useCallback(async () => {
    if (!accountId) return;
    setIsLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/playbooks/accounts/${accountId}/summary?tenant_id=${tenantId}&user_id=${encodeURIComponent(contextUser?.id || '')}`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        if (response.status === 404) {
          setApiAvailable(false);
          return;
        }
        throw new Error(`API error: ${response.status}`);
      }
      const result = await response.json();
      setData(result);
      setApiAvailable(true);

      const summaryAdHoc = normalizeAdHocFromSummary(result);
      const summaryScheduled = Array.isArray(result?.scheduled_upcoming)
        ? result.scheduled_upcoming
        : [];

      if (summaryAdHoc.total === 0 && summaryScheduled.length === 0) {
        await loadAdHocFallback();
      } else {
        setFallbackAdHocPersonas([]);
        setFallbackScheduled([]);
      }
    } catch (err) {
      console.log('Playbook API not available:', err.message);
      setApiAvailable(false);
    } finally {
      setIsLoading(false);
    }
  }, [accountId, tenantId, contextUser?.id, loadAdHocFallback]);

  useEffect(() => {
    loadPlaybookSummary();
    const interval = setInterval(loadPlaybookSummary, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadPlaybookSummary]);

  const summaryAdHoc = normalizeAdHocFromSummary(data);
  const summaryScheduled = Array.isArray(data?.scheduled_upcoming) ? data.scheduled_upcoming : [];

  const adHocPersonas =
    summaryAdHoc.total > 0 ? summaryAdHoc.personas : fallbackAdHocPersonas;
  const adHocTotal = countAdHocMessages(adHocPersonas);
  const scheduledUpcoming =
    summaryScheduled.length > 0 ? summaryScheduled : fallbackScheduled;
  const scheduledTotal = scheduledUpcoming.length;

  useEffect(() => {
    reportCount(data, adHocPersonas, scheduledUpcoming);
  }, [data, adHocPersonas, scheduledUpcoming, reportCount]);

  if (isLoading && !data && !fallbackAdHocPersonas.length) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#00C2C2]" />
      </div>
    );
  }

  if (!apiAvailable && adHocTotal === 0 && scheduledTotal === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="w-12 h-12 text-gray-300 mb-4" />
        <p className="text-lg font-medium text-[#0A1A2F] mb-2">{t('acctPlaybookTrackingSoon')}</p>
        <p className="text-sm text-[#5C6B7A] max-w-md">
          {t('acctPlaybookTrackingSoonDesc')}
        </p>
      </div>
    );
  }

  const activePlaybooks = data?.active_playbooks || [];
  const completedPlaybooks = data?.completed_playbooks || [];

  if (
    activePlaybooks.length === 0 &&
    completedPlaybooks.length === 0 &&
    adHocTotal === 0 &&
    scheduledTotal === 0 &&
    !loadingAdHocFallback
  ) {
    return (
      <div className="text-center py-12 text-[#5C6B7A]">
        <p className="text-lg font-medium mb-2">{t('acctNoPlaybooksOrOutreach')}</p>
        <p className="text-sm">{t('acctNoPlaybooksOrOutreachDesc')}</p>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      paused: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-gray-100 text-gray-800',
    };
    return styles[String(status || '').toLowerCase()] || styles.active;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (!Number.isFinite(d.getTime())) return 'N/A';
      return format(d, 'MMM d, yyyy');
    } catch {
      return 'N/A';
    }
  };

  const renderPlaybookCard = (playbook, opts = {}) => {
    const isCompleted = !!opts.completed;
    const isEvent = !!(playbook.event_id || playbook.event_name);
    const isDiy = !!playbook.is_diy;
    return (
      <Card key={playbook.playbook_id + (isCompleted ? '-c' : '')} className={`border-0 shadow-md ${isCompleted ? 'opacity-75' : ''}`}>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-lg text-[#0A1A2F]">{playbook.playbook_name}</CardTitle>
                {isEvent && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200">
                    <Calendar className="w-3 h-3 mr-1 inline" />
                    {t('event')}{playbook.event_name ? ` · ${playbook.event_name}` : ''}
                  </Badge>
                )}
                {isDiy && (
                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-800 border-purple-200">
                    <Sparkles className="w-3 h-3 mr-1 inline" />
                    DIY
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-2 text-sm text-[#5C6B7A]">
                <span>👤 {playbook.enrolled_personas_count !== 1
                  ? t('acctEnrolledPersonasMany', { n: playbook.enrolled_personas_count })
                  : t('acctEnrolledPersonasOne', { n: playbook.enrolled_personas_count })}</span>
                {isCompleted
                  ? <span>📅 {t('acctCompletedOn', { date: formatDate(playbook.completed_at) })}</span>
                  : <span>📅 {t('acctStartedOn', { date: formatDate(playbook.started_at) })}</span>}
              </div>
            </div>
            <Badge className={getStatusBadge(playbook.status)}>{playbook.status}</Badge>
          </div>
        </CardHeader>
        {!isCompleted && (
          <CardContent>
            <div className="space-y-3">
              <p className="text-sm font-medium text-[#0A1A2F]">{t('acctEnrolledPersonasLabel')}</p>
              {playbook.enrolled_personas?.map((persona) => {
                const personaProfileId = persona.person_id;
                return (
                  <div key={persona.person_id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-[#0A1A2F]">{persona.first_name} {persona.last_name}</p>
                      <p className="text-sm text-[#5C6B7A]">{persona.role_title}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-[#5C6B7A]">
                        <span>{t('acctTouchOf', { current: persona.current_touch, total: persona.total_touches })}</span>
                        {persona.next_touch_date && <span>• {t('acctNextOn', { date: formatDate(persona.next_touch_date) })}</span>}
                      </div>
                    </div>
                    {personaProfileId ? (
                      <Link to={createPageUrl(`PersonProfile?id=${encodeURIComponent(personaProfileId)}`)}>
                        <Button variant="outline" size="sm">
                          <ExternalLink className="w-3 h-3 mr-1" />
                          {t('viewProfile')}
                        </Button>
                      </Link>
                    ) : (
                      <Button variant="outline" size="sm" disabled>
                        <ExternalLink className="w-3 h-3 mr-1" />
                        {t('viewProfile')}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {loadingAdHocFallback && adHocTotal === 0 && scheduledTotal === 0 && (
        <div className="flex items-center gap-2 text-sm text-[#5C6B7A]">
          <Loader2 className="w-4 h-4 animate-spin text-[#00C2C2]" />
          {t('acctLoadingAdHocOutreach')}
        </div>
      )}

      {activePlaybooks.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg font-semibold text-[#0A1A2F]">🟢 {t('acctActivePlaybooks')}</span>
            <Badge variant="secondary">{activePlaybooks.length}</Badge>
          </div>
          <div className="space-y-4">
            {activePlaybooks.map((pb) => renderPlaybookCard(pb))}
          </div>
        </div>
      )}

      {completedPlaybooks.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg font-semibold text-[#0A1A2F]">⚪ {t('acctCompletedPlaybooks')}</span>
            <Badge variant="secondary">{completedPlaybooks.length}</Badge>
          </div>
          <div className="space-y-4">
            {completedPlaybooks.map((pb) => renderPlaybookCard(pb, { completed: true }))}
          </div>
        </div>
      )}

      {scheduledTotal > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg font-semibold text-[#0A1A2F]">🔵 {t('acctScheduledPending')}</span>
            <Badge variant="secondary">{scheduledTotal}</Badge>
          </div>
          <div className="space-y-2">
            {scheduledUpcoming.map((s) => {
              const key = s.id || `${s.person_id}-${s.scheduled_send_at}-${s.subject}`;
              const scheduledAt = s.scheduled_send_at ?? s.scheduledSendAt;
              return (
                <div key={key} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {isLinkedInOutboxChannel(s)
                        ? <Linkedin className="w-4 h-4 text-blue-700 flex-shrink-0" />
                        : <Mail className="w-4 h-4 text-blue-700 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0A1A2F] truncate">{getOutboxDraftHeadline(s)}</p>
                        <p className="text-xs text-blue-800 mt-0.5">
                          {s.person_name ? `${s.person_name} · ` : ''}
                          {t('acctScheduledFor', { date: formatDate(scheduledAt) })}
                          {scheduledAt && (
                            <>
                              {' '}
                              ·{' '}
                              {new Date(scheduledAt).toLocaleTimeString([], {
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-xs bg-white">
                      {s.source === 'ad_hoc' || s.is_ad_hoc ? t('adHoc') : `${t('playbook')} · T${s.stage_no ?? '?'}`}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {adHocTotal > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg font-semibold text-[#0A1A2F]">✉️ {t('acctAdHocMessages')}</span>
            <Badge variant="secondary">{adHocTotal}</Badge>
          </div>
          <div className="space-y-4">
            {adHocPersonas.map((persona) => (
              <Card key={persona.person_id} className="border-0 shadow-md">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <CardTitle className="text-base text-[#0A1A2F]">
                        {persona.first_name} {persona.last_name}
                      </CardTitle>
                      <p className="text-sm text-[#5C6B7A] mt-1">{persona.role_title}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {(persona.message_count ?? persona.messages?.length ?? 0) !== 1
                          ? t('acctMsgCountMany', { n: persona.message_count ?? persona.messages?.length ?? 0 })
                          : t('acctMsgCountOne', { n: persona.message_count ?? persona.messages?.length ?? 0 })}
                      </Badge>
                      {persona.person_id && (
                        <Link to={createPageUrl(`PersonProfile?id=${encodeURIComponent(persona.person_id)}`)}>
                          <Button variant="outline" size="sm">
                            <ExternalLink className="w-3 h-3 mr-1" />
                            {t('viewProfile')}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(persona.messages || []).map((m) => {
                      const Icon = CHANNEL_ICONS[m.channel] || Mail;
                      return (
                        <div key={m.id || m.step_id} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Icon className="w-4 h-4 text-[#5C6B7A] flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[#0A1A2F] truncate">{m.subject || t('acctManualCompletion')}</p>
                              <p className="text-xs text-[#5C6B7A] mt-0.5">{formatDate(m.sent_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <OutreachEngagementIcons
                              openedAt={m.opened_at}
                              clickedAt={m.clicked_at}
                              repliedAt={m.replied_at}
                            />
                            <Badge variant="outline" className="text-xs">{adHocSourceLabel(m.source, t)}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
